/**
 * O som que avisa que chegou mensagem.
 *
 * ── Por que sem biblioteca ────────────────────────────────────────────────
 *
 * O que uma biblioteca de áudio resolve — fila de sons, sprites, troca de
 * formato, posicionamento espacial — nada disso é usado por um aviso que toca
 * sozinho, curto, uma vez. Sobraria o custo: uma dependência a mais no
 * `npm audit`, no build e na revisão de licença, para embrulhar três linhas de
 * `Audio`.
 *
 * ── Por que os arquivos são GERADOS, e não baixados ──────────────────────
 *
 * Som de banco de áudio chega com licença atrelada, e a licença viaja com o
 * produto até o dia em que alguém precisa provar que podia usá-la. O
 * `tools/gerar-sons.mjs` sintetiza os nossos — os arquivos são nossos, e o
 * código que os produz está no repositório.
 *
 * ── A pessoa escolhe o som (13/10/2026) ──────────────────────────────────
 *
 * Quatro, no controle de notificações do atendimento. A escolha vale para este
 * navegador, como os outros interruptores dali. O padrão é o sino de sempre.
 *
 * ── A regra do navegador que derruba isto se for ignorada ────────────────
 *
 * Nenhum navegador toca áudio antes de a pessoa ter interagido com a página.
 * A promessa do `play()` é REJEITADA, e sem o `catch` isso vira um erro não
 * tratado no console a cada mensagem — ruído que esconde erro de verdade.
 *
 * Então: a falha é engolida de propósito, e o sino visual (o número) continua
 * valendo. O aviso nunca depende só do som. Escolher um som é um clique, e por
 * isso também libera o áudio da página.
 */

/** Os sons, na ordem em que aparecem para escolher. Os arquivos saem do gerador. */
export const SONS = [
  { chave: 'sino', nome: 'Sino', caminho: '/som/sino.wav' },
  { chave: 'gota', nome: 'Gota', caminho: '/som/gota.wav' },
  { chave: 'toque-duplo', nome: 'Toque duplo', caminho: '/som/toque-duplo.wav' },
  { chave: 'suave', nome: 'Suave', caminho: '/som/suave.wav' },
] as const;

export type SomDaNotificacao = (typeof SONS)[number]['chave'];

const SOM_PADRAO: SomDaNotificacao = 'sino';

/** Duas mensagens em sequência não tocam duas vezes — ver `tocarSino`. */
export const INTERVALO_MINIMO_MS = 2_000;

const CHAVE_DO_SILENCIO = 'cliqcrm:sino-silenciado';
const CHAVE_DO_SOM = 'cliqcrm:som-da-notificacao';

/**
 * Um elemento por som, reaproveitado.
 *
 * Criar um por toque deixaria o navegador com uma pilha de elementos vivos numa
 * revenda movimentada, e cada um recomeçaria o download em cache frio.
 */
const elementos = new Map<SomDaNotificacao, HTMLAudioElement>();
let ultimoToque = 0;

function elemento(som: SomDaNotificacao): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  let audio = elementos.get(som);
  if (!audio) {
    const caminho = SONS.find((s) => s.chave === som)?.caminho ?? SONS[0].caminho;
    audio = new Audio(caminho);
    // Volume abaixo do máximo: o som toca em sala compartilhada, e o padrão
    // alto demais faz a pessoa silenciar tudo em vez de ajustar.
    audio.volume = 0.5;
    audio.preload = 'auto';
    elementos.set(som, audio);
  }
  return audio;
}

function ler(chave: string): string | null {
  try {
    return localStorage.getItem(chave);
  } catch {
    // Navegação privada ou armazenamento bloqueado: vale o padrão.
    return null;
  }
}

function gravar(chave: string, valor: string): void {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // Sem onde guardar, a escolha vale só nesta aba. Melhor que estourar.
  }
}

export function sinoEstaSilenciado(): boolean {
  return ler(CHAVE_DO_SILENCIO) === 'sim';
}

export function definirSilencioDoSino(silenciado: boolean): void {
  gravar(CHAVE_DO_SILENCIO, silenciado ? 'sim' : 'nao');
}

/** O som escolhido neste navegador. Valor desconhecido (som que saiu) volta ao padrão. */
export function somEscolhido(): SomDaNotificacao {
  const valor = ler(CHAVE_DO_SOM);
  return SONS.some((s) => s.chave === valor) ? (valor as SomDaNotificacao) : SOM_PADRAO;
}

export function definirSom(som: SomDaNotificacao): void {
  gravar(CHAVE_DO_SOM, som);
}

function tocar(som: SomDaNotificacao): void {
  const audio = elemento(som);
  if (!audio) return;
  audio.currentTime = 0;
  // `void` + `catch`: ver o cabeçalho. Antes da primeira interação da pessoa
  // com a página, o navegador recusa — e isso não é erro nosso.
  void audio.play().catch(() => undefined);
}

/**
 * Toca o som escolhido, se não estiver silenciado e se não tiver tocado agora
 * mesmo.
 *
 * O intervalo mínimo existe porque mensagens chegam em rajada: um cliente manda
 * três linhas seguidas e o fluxo entrega três eventos em dois segundos. Três
 * toques sobrepostos não avisam melhor — irritam, e a pessoa silencia.
 */
export function tocarSino(agora: number = Date.now()): void {
  if (sinoEstaSilenciado()) return;
  if (agora - ultimoToque < INTERVALO_MINIMO_MS) return;
  ultimoToque = agora;
  tocar(somEscolhido());
}

/**
 * A prévia, quando a pessoa escolhe: toca na hora, mesmo com o sino
 * silenciado e mesmo logo depois de uma mensagem — quem clicou para ouvir quer
 * ouvir.
 */
export function ouvirSom(som: SomDaNotificacao): void {
  tocar(som);
}
